import { AnimatePresence, motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale } from '../../../i18n'
import { type SelectOption } from '../../../components/SelectCustom'
import { SkeletonBlock, customerMotionTransition } from '../customer-area'
import {
  type OrderPageSizeOption,
  type OrderSortOption,
  OrdersRowsSkeleton,
} from './ordersListShared'
import type { FilteredOrderRow, TranslateFn } from './ordersListSectionTypes'
import { OrdersResultsEmptyState } from './OrdersResultsEmptyState'
import { OrderResultRow } from './OrderResultRow'
import { OrdersResultsPaginationFooter } from './OrdersResultsPaginationFooter'

type OrdersResultsSectionProps = {
  reducedMotion: boolean
  loading: boolean
  ordersError: string
  searchValue: string
  sortBy: OrderSortOption
  activeFilterChipsLength: number
  orders: CustomerOrder[]
  filteredOrders: FilteredOrderRow[]
  paginatedOrders: FilteredOrderRow[]
  currentPage: number
  totalPages: number
  visiblePages: number[]
  pageSize: OrderPageSizeOption
  pageSizeOptions: SelectOption[]
  pagination: { from: number; to: number }
  locale: ShopLocale
  language: ShopLanguage
  dateFormatter: Intl.DateTimeFormat
  onOpenOrderDetail: (orderId: string) => void
  onBuyAgain: (order: CustomerOrder) => void
  onRequestSupport: (order: CustomerOrder) => void
  onOpenCatalog: () => void
  onClearAllFilters: () => void
  onSetPageSize: (value: OrderPageSizeOption) => void
  onSetCurrentPage: (page: number) => void
  t: TranslateFn
}

export const OrdersResultsSection = ({
  reducedMotion,
  loading,
  ordersError,
  searchValue,
  sortBy,
  activeFilterChipsLength,
  orders,
  filteredOrders,
  paginatedOrders,
  currentPage,
  totalPages,
  visiblePages,
  pageSize,
  pageSizeOptions,
  pagination,
  locale,
  language,
  dateFormatter,
  onOpenOrderDetail,
  onBuyAgain,
  onRequestSupport,
  onOpenCatalog,
  onClearAllFilters,
  onSetPageSize,
  onSetCurrentPage,
  t,
}: OrdersResultsSectionProps) => {
  const skeletonTransition = reducedMotion ? { duration: 0 } : { duration: 0.16, ease: customerMotionTransition.ease }
  const contentTransition = reducedMotion ? { duration: 0 } : { duration: 0.26, ease: customerMotionTransition.ease }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {loading ? (
        <motion.div
          key="orders-loading"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={skeletonTransition}
          className="flex min-h-[14rem] flex-col bg-(--color-bg-card) sm:min-h-[15rem] lg:min-h-[16rem]"
        >
          <OrdersRowsSkeleton reducedMotion={reducedMotion} />
          <div className="border-t border-(--color-border-default) px-3 py-3 sm:px-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SkeletonBlock className="h-8 w-40 rounded-md" />
              <div className="flex items-center gap-1">
                <SkeletonBlock className="h-8 w-14 rounded-[10px]" />
                <SkeletonBlock className="h-8 w-8 rounded-[10px]" />
                <SkeletonBlock className="h-8 w-8 rounded-[10px]" />
                <SkeletonBlock className="h-8 w-14 rounded-[10px]" />
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}

      {!loading && !ordersError && filteredOrders.length === 0 ? (
        <OrdersResultsEmptyState
          reducedMotion={reducedMotion}
          ordersCount={orders.length}
          onOpenCatalog={onOpenCatalog}
          onClearAllFilters={onClearAllFilters}
          t={t}
        />
      ) : null}

      {!loading && paginatedOrders.length > 0 ? (
        <motion.div
          key={`orders-list-${currentPage}-${searchValue}-${sortBy}-${activeFilterChipsLength}`}
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={contentTransition}
          className="flex min-h-[14rem] flex-col bg-(--color-bg-card) sm:min-h-[15rem] lg:min-h-[16rem]"
        >
          <motion.ul className="space-y-3 p-3 sm:p-4">
            <AnimatePresence initial={false}>
              {paginatedOrders.map((row, rowIndex) => (
                <OrderResultRow
                  key={row.order.id}
                  row={row}
                  rowIndex={rowIndex}
                  reducedMotion={reducedMotion}
                  locale={locale}
                  language={language}
                  dateFormatter={dateFormatter}
                  onOpenOrderDetail={onOpenOrderDetail}
                  onBuyAgain={onBuyAgain}
                  onRequestSupport={onRequestSupport}
                  t={t}
                />
              ))}
            </AnimatePresence>
          </motion.ul>

          <OrdersResultsPaginationFooter
            reducedMotion={reducedMotion}
            currentPage={currentPage}
            totalPages={totalPages}
            visiblePages={visiblePages}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            pagination={pagination}
            totalItems={filteredOrders.length}
            onSetPageSize={onSetPageSize}
            onSetCurrentPage={onSetCurrentPage}
            t={t}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
